import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  ImageList,
  ImageListItem,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { portalApi, ServiceDetail as ServiceDetailType } from '../services/portalApi';
import { InspectionReport } from '../components/InspectionReport';
import { normalizeReportChecklist } from '../utils/serviceChecklist';
import { formatStatus } from '../utils/formatStatus';
import { formatServiceDuration } from '../utils/formatServiceDuration';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';

type ChemicalAddedDisplay = {
  name: string;
  amount?: unknown;
  unit?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const formatChemicalAmount = (amount: unknown) => {
  if (amount === null || amount === undefined || amount === '') return '';
  if (typeof amount !== 'number') return String(amount);

  return Number(amount.toFixed(1)).toLocaleString('en-US', {
    maximumFractionDigits: 1,
  });
};

const formatChemicalName = (name: string, unit?: string) => {
  // Catalog entries store "productId::Display Name (unit)" as their key.
  const separatorIndex = name.indexOf('::');
  const displayName = separatorIndex >= 0 ? name.slice(separatorIndex + 2) : name;
  return unit ? `${displayName} (${unit})` : displayName;
};

const normalizeChemicalsAdded = (chemicalsAdded: unknown): ChemicalAddedDisplay[] => {
  if (Array.isArray(chemicalsAdded)) {
    return chemicalsAdded
      .map((chemical) => {
        if (isRecord(chemical)) {
          const name = chemical.name || chemical.chemical;
          if (!name) return null;
          return {
            name: String(name),
            amount: chemical.amount,
            unit: chemical.unit ? String(chemical.unit) : undefined,
          };
        }

        return { name: String(chemical) };
      })
      .filter((chemical): chemical is ChemicalAddedDisplay => Boolean(chemical));
  }

  if (isRecord(chemicalsAdded)) {
    return Object.entries(chemicalsAdded)
      .filter(([, amount]) => amount !== null && amount !== undefined && amount !== '')
      .map(([name, amount]) => ({ name, amount }));
  }

  return [];
};

export const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [service, setService] = useState<ServiceDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const data = await portalApi.getServiceDetail(id);
        setService(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const chemicalLabels: Record<string, { label: string; unit: string; ideal: string }> = {
    chlorine: { label: 'Free Chlorine', unit: 'ppm', ideal: '1-3' },
    ph: { label: 'pH', unit: '', ideal: '7.2-7.6' },
    alkalinity: { label: 'Total Alkalinity', unit: 'ppm', ideal: '80-120' },
    calciumHardness: { label: 'Calcium Hardness', unit: 'ppm', ideal: '200-400' },
    cyanuricAcid: { label: 'Cyanuric Acid', unit: 'ppm', ideal: '30-50' },
    salt: { label: 'Salt', unit: 'ppm', ideal: '2700-3400' },
    waterTemp: { label: 'Water Temp', unit: '°F', ideal: '-' },
    phosphates: { label: 'Phosphates', unit: 'ppb', ideal: '<100' },
    psi: { label: 'Filter PSI', unit: 'psi', ideal: '-' },
  };

  if (loading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/service-history')} sx={{ mb: 2 }}>
          {t('Back')}
        </Button>
        <Card>
          <CardContent>
            <Skeleton width="50%" height={32} />
            <Skeleton width="80%" height={24} />
            <Skeleton width="40%" height={24} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error || !service) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/service-history')} sx={{ mb: 2 }}>
          {t('Back')}
        </Button>
        <Alert severity="error">{error || t('Service record not found')}</Alert>
      </Box>
    );
  }

  const checklistItems = normalizeReportChecklist(service.checklistItems);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/service-history')} sx={{ mb: 2 }}>
        {t('Back to Service History')}
      </Button>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h5">
              {formatDate(service.serviceDate)}
            </Typography>
            <Chip
              label={t(formatStatus(service.status))}
              color={service.status === 'completed' ? 'success' : service.status === 'skipped' ? 'warning' : 'default'}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">{t('Property')}</Typography>
              <Typography>{service.property.reportLabel || service.property.name || service.property.address}</Typography>
            </Box>
            {service.bodyOfWater && (
              <Box>
                <Typography variant="body2" color="text.secondary">{t('Body of Water')}</Typography>
                <Typography>{service.bodyOfWater.name}</Typography>
              </Box>
            )}
            {service.showTechnicianAndTiming && service.technicianName && (
              <Box>
                <Typography variant="body2" color="text.secondary">{t('Technician')}</Typography>
                <Typography>{service.technicianName}</Typography>
              </Box>
            )}
            {service.showTechnicianAndTiming && service.arrivedAt && service.completedAt && (
              <Box>
                <Typography variant="body2" color="text.secondary">{t('Service Time')}</Typography>
                <Typography>
                  {formatTime(service.arrivedAt)} - {formatTime(service.completedAt)}
                  {formatServiceDuration(service.durationMinutes)}
                </Typography>
              </Box>
            )}
          </Box>

          {service.skipReason && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('Service skipped')}: {service.skipReason}
            </Alert>
          )}
        </CardContent>
      </Card>

      {service.inspectionData && <InspectionReport report={service.inspectionData} document={service.reportDocument} />}

      {/* Tech Notes */}
      {service.techNotes && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('Technician Notes')}</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{service.techNotes}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Checklist + Chemical Readings + Chemicals Added — 3 columns */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        {checklistItems.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('Service Checklist')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {checklistItems.map((item, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {item.completed ? (
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    ) : (
                      <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    )}
                    <Typography
                      sx={{
                        textDecoration: item.completed ? 'none' : 'line-through',
                        color: item.completed ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
        {service.chemicalReading && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('Chemical Readings')}</Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(chemicalLabels).map(([key, { label, unit, ideal }]) => {
                    const value = service.chemicalReading?.[key as keyof typeof service.chemicalReading];
                    if (value === null || value === undefined) return null;
                    return (
                      <TableRow key={key}>
                        <TableCell sx={{ fontWeight: 500 }}>{t(label)}</TableCell>
                        <TableCell align="right">
                          {parseFloat(Number(value).toFixed(1))} {unit}
                        </TableCell>
                        {service.showIdealChemicalRanges && (
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            {t('Ideal')}: {ideal}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
        {service.chemicalReading?.chemicalsAdded && normalizeChemicalsAdded(service.chemicalReading.chemicalsAdded).length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('Chemicals Added')}</Typography>
              <Table size="small">
                <TableBody>
                  {normalizeChemicalsAdded(service.chemicalReading.chemicalsAdded).map((chemical) => (
                    <TableRow key={formatChemicalName(chemical.name, chemical.unit)}>
                      <TableCell sx={{ fontWeight: 500 }}>{formatChemicalName(chemical.name, chemical.unit)}</TableCell>
                      <TableCell align="right">
                        {formatChemicalAmount(chemical.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Photos */}
      {service.photos && service.photos.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('Service Photos')}</Typography>
            <ImageList cols={3} gap={8}>
              {service.photos.map((photo, index) => (
                <ImageListItem
                  key={photo.id}
                  sx={{ cursor: 'pointer', borderRadius: 1, overflow: 'hidden' }}
                  onClick={() => {
                    setCurrentPhotoIndex(index);
                    setLightboxOpen(true);
                  }}
                >
                  <img
                    src={photo.thumbnailUrl || photo.url}
                    alt={photo.caption || `${t('Photo')} ${index + 1}`}
                    loading="lazy"
                    style={{ aspectRatio: '1', objectFit: 'cover' }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </CardContent>
        </Card>
      )}

      {/* Photo Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ position: 'relative', bgcolor: 'black' }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}
            onClick={() => setLightboxOpen(false)}
          >
            <CloseIcon />
          </IconButton>

          {service.photos && service.photos.length > 1 && (
            <>
              <IconButton
                sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'white' }}
                onClick={() => setCurrentPhotoIndex((prev) => (prev === 0 ? service.photos!.length - 1 : prev - 1))}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'white' }}
                onClick={() => setCurrentPhotoIndex((prev) => (prev === service.photos!.length - 1 ? 0 : prev + 1))}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}

          {service.photos && service.photos[currentPhotoIndex] && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <img
                src={service.photos[currentPhotoIndex].url}
                alt={service.photos[currentPhotoIndex].caption || `${t('Photo')} ${currentPhotoIndex + 1}`}
                style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            </Box>
          )}

          {service.photos && service.photos[currentPhotoIndex]?.caption && (
            <Typography sx={{ color: 'white', p: 2, textAlign: 'center' }}>
              {service.photos[currentPhotoIndex].caption}
            </Typography>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};
